import { useState } from 'react';
import { Alert, Button, EmptyState, Field, Modal, Pagination, cn } from '@evogard/ui';
import type { ManagementClaim } from '@evogard/contract';
import { MANAGEMENT_AGE_LIMITS, type ManagementStats } from '@evogard/domain';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { describeClaim } from './ClaimDot.js';
import {
  LINE_LABEL,
  LINE_TONE,
  ageText,
  daysSince,
  initialsOf,
  numberOf,
  stallLabel,
} from './managementLabels.js';
import {
  useManagementGroup,
  type ManagementOnly,
  type ManagementPoint,
} from './useManagement.js';

/** As faixas de idade, pelo último dia de cada uma — a mesma régua do domínio. */
const AGE_LABELS = [
  `até ${MANAGEMENT_AGE_LIMITS[0]} dias`,
  `${MANAGEMENT_AGE_LIMITS[0] + 1} a ${MANAGEMENT_AGE_LIMITS[1]}`,
  `${MANAGEMENT_AGE_LIMITS[1] + 1} a ${MANAGEMENT_AGE_LIMITS[2]}`,
  `mais de ${MANAGEMENT_AGE_LIMITS[2]}`,
];

const PER_PAGE = 25;

/**
 * A lista de um ponto do trilho — a etapa, a estação ou um marco —, sem sair
 * do mapa: os números do ponto, quantos há em cada faixa de idade na fase, os
 * filtros e os acionamentos do mais antigo na fase ao mais novo. O clique
 * abre a mesa.
 */
export function PointPanel({
  point,
  title,
  stats,
  initialOnly,
  canOpen,
  onOpenClaim,
  onClose,
}: {
  point: ManagementPoint | null;
  title: string;
  /** O filtro com que a lista abre — a linha "Parados" da caixa abre só eles. */
  initialOnly?: ManagementOnly;
  /** Os números que o mapa já tem deste ponto — o painel não os pede de novo. */
  stats: ManagementStats | null;
  canOpen: (claim: ManagementClaim) => boolean;
  onOpenClaim: (claim: ManagementClaim) => void;
  onClose: () => void;
}) {
  const [only, setOnly] = useState<ManagementOnly | undefined>(initialOnly);
  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search).trim();
  const [page, setPage] = useState(1);
  const query = useManagementGroup(
    point,
    { only, search: term || undefined },
    page,
    PER_PAGE,
  );
  const result = query.data;

  const filters: { value: ManagementOnly | undefined; label: string; count?: number }[] =
    [
      { value: undefined, label: 'Todos', count: stats?.total },
      { value: 'sla_expirado', label: 'SLA expirado', count: stats?.slaExpired },
      { value: 'parado', label: 'Parados', count: stats?.onHold },
      ...(point?.line === 'perifericos'
        ? []
        : [
            {
              value: 'terceiro' as const,
              label: 'De terceiros',
              count: stats?.thirdParty,
            },
          ]),
    ];

  return (
    <Modal
      open={point !== null}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={title}
      descricao={point ? LINE_LABEL[point.line] : undefined}
    >
      <div className="flex flex-col gap-4">
        {stats && (
          <>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Figure label="acionamentos" value={stats.total} />
              <Figure label="com SLA expirado" value={stats.slaExpired} alarm />
              <Figure label="dias em média na fase" value={stats.averageDays} />
              <Figure label="dias o mais antigo" value={stats.oldestDays} />
            </dl>
            <div
              aria-label="Quantos há quantos dias na fase"
              className="flex flex-col gap-1"
            >
              {stats.ages.map((n, i) => (
                <div
                  key={AGE_LABELS[i]}
                  className="grid grid-cols-[minmax(0,1fr)_6rem_2rem] items-center gap-2 text-xs text-texto-suave"
                >
                  <span className="h-2 overflow-hidden rounded-pilula bg-superficie-suave">
                    {/* Neutra: o vermelho da Gestão é só o SLA expirado. */}
                    <span
                      className="block h-full bg-texto-tenue"
                      style={{
                        width: `${stats.total ? Math.round((n / stats.total) * 100) : 0}%`,
                      }}
                    />
                  </span>
                  <span>{AGE_LABELS[i]}</span>
                  <span className="text-right font-semibold text-texto tabular-nums">
                    {n}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <div
          role="group"
          aria-label="Filtrar os acionamentos"
          className="flex flex-wrap gap-2"
        >
          {filters.map((f) => (
            <Button
              key={f.label}
              size="sm"
              variant={only === f.value ? 'secundario' : 'discreto'}
              aria-pressed={only === f.value}
              onClick={() => {
                setOnly(f.value);
                setPage(1);
              }}
            >
              {f.label}
              {f.count !== undefined && (
                <span className="ml-1 tabular-nums text-texto-suave">{f.count}</span>
              )}
            </Button>
          ))}
        </div>
        <Field
          label="Buscar neste ponto"
          labelHidden
          type="search"
          icon="busca"
          placeholder="Número, nome ou placa"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
        {query.isLoading && <p className="text-sm text-texto-suave">Carregando…</p>}
        {result && result.items.length === 0 && (
          <EmptyState icon="busca" title="Nenhum acionamento">
            Nenhum acionamento neste ponto com esse filtro.
          </EmptyState>
        )}
        {result && result.items.length > 0 && (
          <>
            <p className="text-xs text-texto-suave">
              O mais antigo na fase primeiro.
              {!canOpen(result.items[0]!) &&
                ' Abrir a mesa pede a permissão de ver o sinistro ou o acionamento.'}
            </p>
            <ul className="flex flex-col divide-y divide-borda-suave">
              {result.items.map((c) => (
                <li key={`${c.source}-${c.id}`}>
                  <Row claim={c} openable={canOpen(c)} onOpen={() => onOpenClaim(c)} />
                </li>
              ))}
            </ul>
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              label="acionamentos"
            />
          </>
        )}
      </div>
    </Modal>
  );
}

/** Um número do ponto: o valor em cima, o rótulo embaixo — `dt` antes de `dd` no DOM. */
function Figure({
  label,
  value,
  alarm,
}: {
  label: string;
  value: number;
  alarm?: boolean;
}) {
  return (
    <div className="flex flex-col-reverse rounded-borda border border-borda px-3 py-2">
      <dt className="text-xs text-texto-suave">{label}</dt>
      <dd
        className={cn(
          'text-lg font-bold tabular-nums',
          alarm && value > 0 && 'text-erro',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Row({
  claim: c,
  openable,
  onOpen,
}: {
  claim: ManagementClaim;
  openable: boolean;
  onOpen: () => void;
}) {
  const days = daysSince(c.phaseEnteredAt);
  return (
    <button
      type="button"
      disabled={!openable}
      onClick={onOpen}
      aria-label={describeClaim(c)}
      className="flex w-full items-center gap-3 px-1 py-2 text-left transition-colors enabled:hover:bg-superficie-suave disabled:cursor-default"
    >
      <span
        aria-hidden
        className={cn(
          'grid size-7 shrink-0 place-items-center rounded-pilula border-2 bg-superficie text-[0.625rem] font-bold',
          LINE_TONE[c.line].border,
          c.type === 'terceiro' && 'border-dashed',
          c.slaExpired && 'border-erro text-erro',
        )}
      >
        {initialsOf(c.name)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-texto">
          {c.name ?? 'sem nome'}
        </span>
        <span className="truncate text-xs text-texto-suave">
          {numberOf(c)}
          {c.plate && (
            <>
              {' · '}
              <span className="font-mono tabular-nums">{c.plate}</span>
            </>
          )}
          {c.type === 'terceiro' && ` · terceiro do #${c.parentClaimId}`}
          {c.stalled &&
            ` · parado: ${stallLabel(c.line, c.hold ? { kind: 'parada', code: c.hold } : { kind: 'situacao', code: c.situation ?? '' })}`}
        </span>
      </span>
      <span
        className={cn(
          'shrink-0 text-xs font-semibold tabular-nums',
          c.slaExpired ? 'text-erro' : 'text-texto-suave',
        )}
      >
        {ageText(days)}
      </span>
    </button>
  );
}
